
import { useState } from 'react';

export default function FruitHeightsContact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6gidghr3etgg8tahbs0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', address: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="fruit-heights-contact" className="py-12 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          <div className="order-2 lg:order-1">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
              Get Your Free Fruit Heights Water Damage Assessment
            </h2>
            <p className="text-base md:text-xl text-gray-600 mb-6 md:mb-8">
              24/7 emergency water damage restoration services throughout Fruit Heights, Utah. Call now or request a free estimate.
            </p>

            <div className="space-y-4 md:space-y-6">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-lg md:text-xl text-sky-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1 text-sm md:text-base">24/7 Emergency Line</div>
                  <a href="tel:3853406632" className="text-xl md:text-2xl font-bold text-sky-600 hover:text-sky-700 cursor-pointer">
                    (385) 340-6632
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-fill text-lg md:text-xl text-sky-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1 text-sm md:text-base">Service Area</div>
                  <div className="text-sm md:text-base text-gray-600">
                    All of Fruit Heights, Utah<br />
                    Including Nicholls Park &amp; Oak Hills Areas
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-fill text-lg md:text-xl text-sky-600"></i>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 mb-1 text-sm md:text-base">Response Time</div>
                  <div className="text-sm md:text-base text-gray-600">
                    Within 60 minutes for Fruit Heights emergencies
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 bg-white rounded-xl sm:rounded-2xl shadow-lg p-5 sm:p-6 md:p-8">
            <h3 className="text-xl font-bold text-sky-600 mb-4">Fill Out for Free Estimate:</h3>
            <form id="fruit-heights-contact-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label htmlFor="fh-name" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fh-name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="fh-phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="fh-phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                    placeholder="(801) 555-0123"
                  />
                </div>

                <div>
                  <label htmlFor="fh-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="fh-email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="fh-address" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Property Address
                  </label>
                  <input
                    type="text"
                    id="fh-address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
                    placeholder="123 Main St, Fruit Heights, UT"
                  />
                </div>

                <div>
                  <label htmlFor="fh-message" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Describe Your Situation
                  </label>
                  <textarea
                    id="fh-message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your situation..."
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sky-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-sky-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Request Free Assessment'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm">
                    Thank you! We'll contact you shortly to schedule your free assessment.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm">
                    Something went wrong. Please call us at (385) 340-6632 for immediate assistance.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
